import debounce from 'lodash/debounce';
import {RefObject, useEffect, useMemo} from 'react';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import {tryUpdateHiddenElement} from '../util/element';

export default function useOnScrollAndResize({
  scrollRef,
  onResize,
  onScroll,
  disabled,
  resizeDebounceTime,
}: {
  scrollRef: RefObject<HTMLElement>;
  onResize?: () => void;
  onScroll?: () => void;
  disabled?: boolean;
  resizeDebounceTime?: number;
}) {
  useIsomorphicLayoutEffect(() => {
    const disconnect =
      !disabled && onResize
        ? tryUpdateHiddenElement(scrollRef, onResize)
        : undefined;
    return () => disconnect?.();
  }, [scrollRef, onResize, disabled]);

  const onDebouncedResizeHandler = useMemo(
    () =>
      onResize && resizeDebounceTime
        ? debounce(onResize, resizeDebounceTime)
        : onResize,
    [onResize, resizeDebounceTime]
  );
  useEffect(() => {
    const el = scrollRef.current;
    if (disabled || !el) return;

    onDebouncedResizeHandler &&
      window.addEventListener('resize', onDebouncedResizeHandler);
    onScroll && el.addEventListener('scroll', onScroll);
    return () => {
      onDebouncedResizeHandler &&
        window.removeEventListener('resize', onDebouncedResizeHandler);
      onScroll && el.removeEventListener('scroll', onScroll);
    };
  }, [scrollRef, onDebouncedResizeHandler, onScroll, disabled]);
}
